import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { accountingApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

type Tab = 'liability' | 'returns';

/**
 * Tax liability + returns — FR-ACC-09
 */
export function TaxPage() {
  const [tab, setTab] = useState<Tab>('liability');

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'liability', label: 'Tax liability' },
    { key: 'returns',   label: 'Tax returns' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Accounting · Compliance"
        title="Tax"
        subtitle="Review tax liability by class and jurisdiction, and snapshot returns for filing."
      />

      <div className="card p-1.5 mb-6 inline-flex gap-1 border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={clsx(
              'px-4 py-1.5 text-sm font-semibold rounded-pill transition-all',
              tab === t.key
                ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
                : 'text-slate-600 hover:bg-slate-50'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'liability' && <LiabilityTab />}
      {tab === 'returns' && <ReturnsTab />}
    </div>
  );
}

function firstOfMonth(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function lastOfMonth(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}

// ============================================================
// Liability tab
// ============================================================
function LiabilityTab() {
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(lastOfMonth());

  const q = useQuery({
    queryKey: ['tax-liability', fromDate, toDate],
    queryFn: () => accountingApi.getTaxLiability({ fromDate, toDate }),
  });

  return (
    <div>
      {/* Date range card */}
      <div className="card p-4 mb-6 flex gap-3 items-end flex-wrap border-slate-200">
        <div>
          <label className="label">From</label>
          <input type="date" className="input w-44" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input w-44" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div className="text-xs text-slate-500 max-w-sm">
          Numbers are live — changing dates recomputes liability from journal entries in the range.
        </div>
      </div>

      {q.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : q.error ? (
        <ErrorState error={q.error} />
      ) : !q.data?.classes.length ? (
        <div className="card">
          <EmptyState
            message="No tax classes configured"
            hint="Configure tax classes in Admin settings to see liability here."
          />
        </div>
      ) : (
        <>
          {/* KPI strip — collected/paid/net is the auditor's story */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <KpiTile label="Collected (output)" value={`${fmtCurrency(q.data.totals.totalCollected)}`} accent="sage" />
            <KpiTile label="Paid (input)" value={`${fmtCurrency(q.data.totals.totalPaid)}`} accent="danger" />
            <KpiTile label="Net payable"
              value={`${fmtCurrency(q.data.totals.netPayable)}`}
              accent={q.data.totals.netPayable >= 0 ? 'finance' : 'sage'}
              sublabel={q.data.totals.netPayable < 0 ? 'Net refund owed to us' : undefined} />
          </div>

          {/* Class breakdown */}
          <div className="card overflow-hidden border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Class</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Type</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Jurisdiction</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Rate</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-sage-700">Collected</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-danger">Paid</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-700">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(q.data?.classes ?? []).map((c: any, i: number) => (
                    <tr key={c.taxClassId} className={i % 2 ? 'bg-slate-50/40' : 'bg-white'}>
                      <td className="px-4 py-2.5">
                        <div className="font-mono text-xs font-semibold text-slate-900">{c.code}</div>
                        <div className="text-2xs text-slate-500 mt-0.5">{c.name}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="font-mono text-2xs font-semibold uppercase tracking-wider text-slate-700">
                          {c.taxType}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-slate-700">{c.jurisdiction}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-700 tabular-nums">
                        {c.ratePercent}%
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums">
                        {c.collected > 0 ? (
                          <span className="text-sage-700 font-medium">{fmtCurrency(c.collected)}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums">
                        {c.paid > 0 ? (
                          <span className="text-danger font-medium">{fmtCurrency(c.paid)}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-900 tabular-nums">
                        ${c.netPayable.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 border-t-2 border-slate-300">
                    <td colSpan={4} className="px-4 py-3 eyebrow text-slate-700">Totals</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-sage-700 tabular-nums">
                      ${q.data.totals.totalCollected.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-danger tabular-nums">
                      ${q.data.totals.totalPaid.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-display font-bold text-lg text-slate-900 tabular-nums">
                      ${q.data.totals.netPayable.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// Returns tab
// ============================================================
function ReturnsTab() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const listQuery = useQuery({ queryKey: ['tax-returns'], queryFn: () => accountingApi.listTaxReturns() });

  const { filteredRows, kpis } = useMemo(() => {
    const rows = listQuery.data ?? [];
    let draft = 0, filed = 0, totalFiledPayable = 0;
    for (const r of rows) {
      if (r.status === 'draft') draft++;
      else if (r.status === 'filed') { filed++; totalFiledPayable += parseFloat(r.net_payable) || 0; }
    }
    const filtered = statusFilter ? rows.filter((r: any) => r.status === statusFilter) : rows;
    return { filteredRows: filtered, kpis: { total: rows.length, draft, filed, totalFiledPayable } };
  }, [listQuery.data, statusFilter]);

  const STATUS_OPTIONS = [
    { value: '',        label: 'All' },
    { value: 'draft',   label: 'Draft' },
    { value: 'filed',   label: 'Filed' },
    { value: 'amended', label: 'Amended' },
    { value: 'void',    label: 'Void' },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total returns" value={kpis.total} />
        <KpiTile label="Draft" value={kpis.draft}
          accent={kpis.draft > 0 ? 'amber' : 'default'} />
        <KpiTile label="Filed" value={kpis.filed} accent="sage" />
        <KpiTile label="Filed value" value={`${fmtCurrency(kpis.totalFiledPayable)}`} accent="finance" />
      </div>

      <div className="card p-1.5 mb-3 flex gap-1 items-center overflow-x-auto border-slate-200">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={clsx(
              'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
              statusFilter === s.value
                ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
                : 'text-slate-600 hover:bg-slate-50'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex justify-end mb-4">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ Create return</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_32rem] gap-6">
        <div className="card overflow-hidden border-slate-200">
          {listQuery.isLoading ? (
            <LoadingState rows={4} />
          ) : listQuery.error ? (
            <ErrorState error={listQuery.error} />
          ) : !filteredRows.length ? (
            <EmptyState
              message={statusFilter ? `No ${statusFilter} returns` : 'No tax returns filed yet'}
              hint={statusFilter ? 'Try a different filter.' : 'Snapshot the current liability as a return to file with the authority.'}
              action={!statusFilter && (
                <button className="btn-primary" onClick={() => setShowCreate(true)}>+ Create return</button>
              )}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Return #</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Type</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Period</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Payable</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRows.map((r: any, i: number) => (
                    <tr
                      key={r.id}
                      onClick={() => setSelected(r.id)}
                      className={clsx(
                        'cursor-pointer transition-colors',
                        selected === r.id
                          ? 'bg-finance-50'
                          : i % 2 ? 'bg-slate-50/40 hover:bg-slate-50' : 'bg-white hover:bg-slate-50'
                      )}
                    >
                      <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-900">{r.return_number}</td>
                      <td className="px-4 py-2">
                        <span className="font-mono text-2xs font-semibold uppercase tracking-wider text-slate-700">
                          {r.tax_type}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-600 tabular-nums">
                        {new Date(r.period_from).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        <span className="mx-1 text-slate-400">–</span>
                        {new Date(r.period_to).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </td>
                      <td className="px-4 py-2"><ReturnStatusBadge status={r.status} /></td>
                      <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">
                        ${parseFloat(r.net_payable).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px] border-slate-200">
          {!selected ? (
            <EmptyState
              message="Select a return"
              hint="Click a row on the left to view class breakdown and filing actions."
            />
          ) : (
            <ReturnDetail
              returnId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['tax-returns'] })}
            />
          )}
        </div>
      </div>

      {showCreate && <CreateReturnModal
        onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          qc.invalidateQueries({ queryKey: ['tax-returns'] });
          setShowCreate(false);
          setSelected(id);
        }}
      />}
    </div>
  );
}

function ReturnStatusBadge({ status }: { status: string }) {
  const cls = {
    draft:   'bg-slate-100 text-slate-700 ring-slate-200',
    filed:   'bg-sage-50 text-sage-700 ring-sage-200',
    amended: 'bg-amber-50 text-amber-800 ring-amber-200',
    void:    'bg-red-50 text-red-700 ring-red-200',
  }[status] ?? 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span className={clsx(
      'inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset',
      cls
    )}>
      {status}
    </span>
  );
}

// ============================================================
// ReturnDetail
// ============================================================
function ReturnDetail({ returnId, onChanged }: { returnId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const [showFile, setShowFile] = useState(false);
  const [showVoid, setShowVoid] = useState(false);

  const q = useQuery({ queryKey: ['tax-return', returnId], queryFn: () => accountingApi.getTaxReturn(returnId) });

  const fileMut = useMutation({
    mutationFn: (fileRef?: string) => accountingApi.fileTaxReturn(returnId, fileRef),
    onSuccess: () => {
      toast.success('Filed');
      qc.invalidateQueries({ queryKey: ['tax-return', returnId] });
      onChanged();
      setShowFile(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const voidMut = useMutation({
    mutationFn: (reason: string) => accountingApi.voidTaxReturn(returnId, reason),
    onSuccess: () => {
      toast.success('Voided');
      qc.invalidateQueries({ queryKey: ['tax-return', returnId] });
      onChanged();
      setShowVoid(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <ErrorState error={q.error} />;
  const r = q.data;
  const details = Array.isArray(r.details) ? r.details : [];
  const netPayable = parseFloat(r.net_payable);

  return (
    <div>
      <div className="mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="eyebrow text-finance-700 mb-1">Tax return</div>
            <div className="font-mono font-bold text-lg text-slate-900">{r.return_number}</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-2xs font-semibold uppercase tracking-wider text-slate-700">
                {r.tax_type}
              </span>
              <span className="text-xs text-slate-500">· {r.jurisdiction}</span>
            </div>
            <div className="font-mono text-xs text-slate-500 mt-1 tabular-nums">
              {new Date(r.period_from).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              <span className="mx-1 text-slate-400">–</span>
              {new Date(r.period_to).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
          <ReturnStatusBadge status={r.status} />
        </div>
      </div>

      {/* 3-tile liability summary */}
      <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-200">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="eyebrow text-sage-700">Collected</div>
            <div className="font-mono font-bold text-lg text-sage-700 tabular-nums mt-1">
              ${parseFloat(r.total_collected).toFixed(2)}
            </div>
          </div>
          <div>
            <div className="eyebrow text-danger">Paid</div>
            <div className="font-mono font-bold text-lg text-danger tabular-nums mt-1">
              ${parseFloat(r.total_paid).toFixed(2)}
            </div>
          </div>
          <div>
            <div className="eyebrow text-slate-700">Net payable</div>
            <div className="font-display font-bold text-2xl text-slate-900 tabular-nums tracking-tight mt-0.5">
              ${netPayable.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown by class */}
      {details.length > 0 && (
        <div className="mb-4">
          <div className="eyebrow text-slate-500 mb-2">Breakdown by class</div>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Class</th>
                  <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Rate</th>
                  <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Collected</th>
                  <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Paid</th>
                  <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {details.map((d: any, i: number) => (
                  <tr key={i} className={i % 2 ? 'bg-slate-50/40' : 'bg-white'}>
                    <td className="px-3 py-2">
                      <div className="font-mono font-semibold text-slate-900">{d.code}</div>
                      <div className="text-2xs text-slate-500 mt-0.5">{d.name}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{d.ratePercent}%</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-sage-700">
                      {(d.collected ?? 0) > 0 ? `${fmtCurrency(d.collected)}` : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-danger">
                      {(d.paid ?? 0) > 0 ? `${fmtCurrency(d.paid)}` : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">
                      ${(d.netPayable ?? 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filing metadata */}
      {r.filed_at && (
        <div className="bg-sage-50 border border-sage-200 rounded-xl p-3 mb-4 text-xs text-sage-800">
          <div className="font-semibold">Filed</div>
          <div className="mt-0.5">
            {new Date(r.filed_at).toLocaleString()} · {r.filed_by_name}
          </div>
          {r.file_reference && (
            <div className="mt-1 font-mono">
              Ref: <span className="font-semibold">{r.file_reference}</span>
            </div>
          )}
        </div>
      )}

      {r.notes && (
        <div className="text-xs text-slate-600 mb-4 whitespace-pre-line bg-slate-50 border border-slate-200 rounded-xl p-3">
          {r.notes}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        {r.status === 'draft' && (
          <>
            <button
              className="text-xs font-semibold text-danger hover:bg-red-50 px-3 py-1.5 rounded-pill transition-colors"
              onClick={() => setShowVoid(true)}
            >
              Void
            </button>
            <button
              className="btn-primary"
              onClick={() => setShowFile(true)}
            >
              Mark as filed
            </button>
          </>
        )}
        {r.status === 'filed' && (
          <button
            className="text-xs font-semibold text-danger hover:bg-red-50 px-3 py-1.5 rounded-pill transition-colors"
            onClick={() => setShowVoid(true)}
          >
            Void
          </button>
        )}
      </div>

      {showFile && (
        <FileDialog
          loading={fileMut.isPending}
          onClose={() => setShowFile(false)}
          onConfirm={(ref) => fileMut.mutate(ref)}
        />
      )}
      {showVoid && (
        <VoidDialog
          loading={voidMut.isPending}
          onClose={() => setShowVoid(false)}
          onConfirm={(reason) => voidMut.mutate(reason)}
        />
      )}
    </div>
  );
}

function FileDialog({
  loading, onClose, onConfirm,
}: { loading: boolean; onClose: () => void; onConfirm: (ref?: string) => void }) {
  const [fileRef, setFileRef] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-finance-700 mb-1">File · Compliance</div>
        <h2 className="text-xl font-bold font-display text-slate-900">Mark as filed</h2>
        <p className="text-sm text-slate-500 mt-2">
          Records that this return has been filed with the tax authority. The numbers freeze — changing tax-class configuration later won't retroactively modify this return.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">File reference <span className="font-normal text-slate-400 normal-case tracking-normal">(optional, e.g. confirmation number)</span></label>
        <input
          className="input font-mono"
          value={fileRef}
          onChange={(e) => setFileRef(e.target.value)}
          placeholder="e.g. CONF-2026-04-Q1-123"
        />
      </div>
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          disabled={loading}
          onClick={() => onConfirm(fileRef || undefined)}
        >
          {loading ? 'Filing…' : 'Mark as filed'}
        </button>
      </div>
    </Modal>
  );
}

function VoidDialog({
  loading, onClose, onConfirm,
}: { loading: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Void · Compliance</div>
        <h2 className="text-xl font-bold font-display text-slate-900">Void tax return</h2>
        <p className="text-sm text-slate-500 mt-2">
          Marks the return as void. It stays on record for audit trail. Use when the return was superseded or filed in error.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="text-danger">*</span></label>
        <textarea
          className="input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Superseded by amended return TR-2026-002"
        />
      </div>
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason)}
        >
          {loading ? 'Voiding…' : 'Void return'}
        </button>
      </div>
    </Modal>
  );
}

function CreateReturnModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [taxType, setTaxType] = useState<'sales' | 'vat' | 'gst' | 'excise' | 'other'>('sales');
  const [jurisdiction, setJurisdiction] = useState('default');
  const [periodFrom, setPeriodFrom] = useState(firstOfMonth());
  const [periodTo, setPeriodTo] = useState(lastOfMonth());
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => accountingApi.createTaxReturn({
      taxType, jurisdiction, periodFrom, periodTo,
      notes: notes || undefined,
    }),
    onSuccess: (d) => { toast.success(`Return ${d.returnNumber} · ${fmtCurrency(d.netPayable)} payable`, { duration: 6000 }); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-finance-700 mb-1">Accounting · Compliance</div>
        <h2 className="text-xl font-bold font-display text-slate-900">Create tax return</h2>
        <p className="text-sm text-slate-500 mt-2 leading-relaxed">
          Snapshots current tax liability for the selected period. Numbers are frozen — future tax-class edits won't retroactively change this return.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Tax type</label>
            <select className="input" value={taxType} onChange={(e) => setTaxType(e.target.value as any)}>
              <option value="sales">Sales tax</option>
              <option value="vat">VAT</option>
              <option value="gst">GST</option>
              <option value="excise">Excise</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="label">Jurisdiction</label>
            <input className="input" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} />
          </div>
          <div>
            <label className="label">Period from</label>
            <input type="date" className="input" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} />
          </div>
          <div>
            <label className="label">Period to</label>
            <input type="date" className="input" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label">Notes <span className="font-normal text-slate-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create draft'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Shared
// ============================================================
function KpiTile({
  label, value, accent = 'default', sublabel,
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'finance' | 'sage' | 'amber' | 'danger';
  sublabel?: string;
}) {
  const bar = {
    default: 'bg-slate-200',
    finance: 'bg-finance-500',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden border-slate-200">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-slate-500">{label}</div>
      <div className="mt-3 text-2xl font-bold font-display text-slate-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sublabel && (
        <div className="text-2xs text-slate-500 mt-0.5">{sublabel}</div>
      )}
    </div>
  );
}
